import { createAxiosInstance } from '@/apis/axios.utils';
import type {
  AddRelationshipAccountParams,
  AddRelationshipTagParams,
  BulkAddRelationshipTagsParams,
  BulkDeleteRelationshipsParams,
  BulkImportCreatorsParams,
  CreateCustomFieldsParams,
  CreateInformationRequestParams,
  CreateManualPaymentParams,
  CreateRelationshipsParams,
  CreateShopifyGiftOrderParams,
  DeleteCustomFieldParams,
  DeleteRelationshipTagParams,
  DeleteTagFromAllRelationshipsParams,
  DownloadCreatorDetailsParams,
  DownloadCreatorPostsParams,
  FetchAccountAvgWeeklyPostsParams,
  FetchAccountMetricsParams,
  FetchAccountParams,
  FetchAccountPostsParams,
  FetchAudienceMetricsParams,
  FetchBrandCreatorParams,
  FetchBrandCreatorsParams,
  FetchBrandedContentParams,
  FetchBrandRelationshipTagsParams,
  FetchCompetitiveMentionPostsParams,
  FetchCompetitiveMentionsParams,
  FetchCreatorAiInsightsParams,
  FetchCreatorsCountParams,
  FetchCustomFieldsParams,
  FetchInfluentialFansParams,
  FetchInformationRequestParams,
  FetchMetaPaidPartnersStatusParams,
  FetchNaturalLanguageSearchAccountsParams,
  FetchPastBrandPartnershipsParams,
  FetchShopifyGiftOrdersParams,
  FetchShopifyProductCategoriesParams,
  FetchShopifyProductListParams,
  FetchSimilarCreatorsParams,
  FindOrCreateCreatorsParams,
  GenerateCreatorAiInsightsParams,
  GetRelationshipTagsParams,
  RemoveRelationshipAccountParams,
  RequestMetaPaidPartnersParams,
  RevokeMetaPaidPartnersParams,
  SearchAccountsForTeaserParams,
  SearchAccountsParams,
  SearchCreatorsParams,
  SearchRelationshipsMultiBrandParams,
  SearchRelationshipsParams,
  ShopifyAccountParams,
  SubmitInformationRequestParams,
  UpdateCreatorDetailsParams,
  UpdateCustomFieldParams,
  UpdateInvitationStatusParams,
} from '@/apis/creators.types';
import { env } from '@/env';
import type { AxiosRequestConfig } from '@/types/axios';

export type * from '@/apis/creators.types';

export const axios = createAxiosInstance(env.creatorsApiUrl, {
  camelizeKeys: true,
});

export async function searchAccounts(
  {
    brandId,
    sources,
    followersGreaterThan,
    followersLessThan,
    bioIncludes,
    bioDoesNotInclude,
    captionsInclude,
    captionsDoNotInclude,
    bioOrCaptionsInclude,
    hasMentionedBrand,
    hasPostedBetween,
    interests,
    paidInterests,
    countryCodes,
    locations,
    handle,
    visualFeatures,
    audience,
    languages,
    minAvgViews,
    maxAvgViews,
    minMedianViews,
    maxMedianViews,
    sortBy,
    minEngagementRate,
    maxEngagementRate,
    limit,
    afterCursor,
    competitiveMentions,
    minCreatorRate,
    maxCreatorRate,
    stateProvinces,
    gender,
    ageBucket,
    minEngagedAccounts,
    maxEngagedAccounts,
    instagram,
  }: SearchAccountsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  const url = `/accounts`;
  const payload = {
    brandId,
    sources,
    followersGreaterThan,
    followersLessThan,
    bioIncludes,
    bioDoesNotInclude,
    captionsInclude,
    captionsDoNotInclude,
    bioOrCaptionsInclude,
    hasMentionedBrand,
    hasPostedBetween,
    interests,
    paidInterests,
    countryCodes,
    locations,
    handle,
    visualFeatures,
    audience,
    languages,
    minAvgViews,
    maxAvgViews,
    minMedianViews,
    maxMedianViews,
    sortBy,
    limit,
    minEngagementRate,
    maxEngagementRate,
    afterCursor,
    competitiveMentions,
    minCreatorRate,
    maxCreatorRate,
    stateProvinces,
    gender,
    ageBucket,
    minEngagedAccounts,
    maxEngagedAccounts,
    instagram,
  };
  return axios.put(url, payload, { ...axiosConfig });
}

export async function searchAccountsForTeaser(
  {
    brandId,
    sources,
    followersGreaterThan,
    followersLessThan,
    bioIncludes,
    bioDoesNotInclude,
    captionsInclude,
    captionsDoNotInclude,
    bioOrCaptionsInclude,
    hasMentionedBrand,
    hasPostedBetween,
    predictedInterests,
    predictedLocations,
    handle,
    visualFeatures,
    sortBy,
  }: SearchAccountsForTeaserParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  const url = `teaser/accounts`;
  const payload = {
    brandId,
    sources,
    followersGreaterThan,
    followersLessThan,
    bioIncludes,
    bioDoesNotInclude,
    captionsInclude,
    captionsDoNotInclude,
    bioOrCaptionsInclude,
    hasMentionedBrand,
    hasPostedBetween,
    predictedInterests,
    estimatedRegions: predictedLocations,
    handle,
    visualFeatures,
    sortBy,
  };
  return axios.put(url, payload, { ...axiosConfig });
}

export async function fetchAccount(
  { source, sourceCreatorId, handle, brandId }: FetchAccountParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get(`/accounts/${source}`, {
    ...axiosConfig,
    params: { brandId, sourceCreatorId, handle },
  });
}

export async function fetchBrandCreators({
  url,
  brandId,
  creatorIds,
  postsPublishedStartDate,
  postsPublishedEndDate,
  instagramPostType,
  youtubePostType,
  afterCursor,
  limit,
}: FetchBrandCreatorsParams) {
  return axios.put(url ?? `/brands/${brandId}/relationships`, {
    creatorIds,
    postsPublishedStartDate,
    postsPublishedEndDate,
    postType: instagramPostType || youtubePostType,
    afterCursor,
    limit,
  });
}

export async function fetchBrandCreator({ url, brandId, creatorId }: FetchBrandCreatorParams) {
  return axios.get(url ?? `/brands/${brandId}/relationships/${creatorId}`);
}

export async function updateCreatorDetails({
  url,
  brandId,
  creatorId,
  creatorDetails,
}: UpdateCreatorDetailsParams) {
  return axios.put(url ?? `/brands/${brandId}/relationships/${creatorId}/details`, creatorDetails);
}

export async function fetchBrandRelationshipTags({
  url,
  brandId,
}: FetchBrandRelationshipTagsParams) {
  return axios.get(url ?? `/brands/${brandId}/relationships/tags`);
}

export async function createInformationRequest({
  brandId,
  creatorId,
}: CreateInformationRequestParams) {
  return axios.put(`/brands/${brandId}/relationships/${creatorId}/information_requests`);
}

export async function fetchInformationRequest({ token }: FetchInformationRequestParams) {
  return axios.get(`/information_requests?token=${token}`);
}

export async function submitInformationRequest(
  { token, creatorData }: SubmitInformationRequestParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  const payload = { token, ...creatorData };
  return axios.put(`/information_requests`, payload, { ...axiosConfig });
}

export async function bulkImportCreators(
  { brandId, creators }: BulkImportCreatorsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post(
    `/brands/${brandId}/relationships/bulk_imports`,
    { creators },
    { ...axiosConfig },
  );
}

export async function bulkDeleteRelationships(
  { brandId, creatorIds }: BulkDeleteRelationshipsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post(
    `/brands/${brandId}/relationships/bulk_delete`,
    { creatorIds },
    { ...axiosConfig },
  );
}

export async function addRelationshipTag(
  { brandId, creatorId, name, color }: AddRelationshipTagParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post(
    `/brands/${brandId}/relationships/${creatorId}/tags`,
    { name, color },
    { ...axiosConfig },
  );
}

export async function bulkAddRelationshipTags(
  { brandId, creatorIds, tags }: BulkAddRelationshipTagsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post(
    `/brands/${brandId}/relationships/tags`,
    { creatorIds, tags },
    { ...axiosConfig },
  );
}

export async function deleteRelationshipTag(
  { brandId, creatorId, tagId }: DeleteRelationshipTagParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.delete(`/brands/${brandId}/relationships/${creatorId}/tags/${tagId}`, {
    ...axiosConfig,
  });
}

export async function deleteTagFromAllRelationships(
  { brandId, tagId }: DeleteTagFromAllRelationshipsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.delete(`/brands/${brandId}/relationships/tags/${tagId}`, {
    ...axiosConfig,
  });
}

export async function searchRelationshipsMultiBrand(
  { brandIds, limit = 60 }: SearchRelationshipsMultiBrandParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  const payload = {
    brand_ids: brandIds,
    limit,
  };

  return axios.put('/relationships', payload, { ...axiosConfig });
}

export async function updateInvitationStatus({
  source,
  sourceCreatorId,
  invitedByBrandId,
  invitationStatus,
  emailContent,
}: UpdateInvitationStatusParams) {
  const params = {
    source,
    invited_by_brand_id: invitedByBrandId,
    source_creator_id: sourceCreatorId,
    invitation_status: invitationStatus,
    email_content: emailContent,
  };

  return axios.put(`/accounts/invitations`, params);
}

export async function searchCreators(
  {
    brandId,
    limit,
    sort,
    lookupName,
    restrictToPlatforms,
    postsPublishedStartDate,
    postsPublishedEndDate,
    tagFilterType,
    tags,
    creatorLocations,
    hasMentionedBrand,
    creatorIds,
    instagramPostType,
    instagramConnectionStatus,
    instagramFollowersGreaterThan,
    instagramFollowersLessThan,
    instagramInterests,
    instagramIsBusiness,
    instagramHandle,
    tiktokConnectionStatus,
    tiktokFollowersGreaterThan,
    tiktokFollowersLessThan,
    tiktokMinEngagementRate,
    tiktokMaxEngagementRate,
    tiktokTTCMStatus,
    tiktokOrganicInterests,
    tiktokPaidInterests,
    tiktokAudienceGender,
    tiktokAudienceAge,
    tiktokMinCreatorRate,
    tiktokMaxCreatorRate,
    tiktokCreatorCurrency,
    tiktokHandle,
    tiktokMinMedianViews,
    tiktokMaxMedianViews,
    youtubeHandle,
    youtubePostType,
    youtubeFollowersGreaterThan,
    youtubeFollowersLessThan,
    youtubeVideoCountGreaterThan,
    youtubeVideoCountLessThan,
    youtubeViewsGreaterThan,
    youtubeViewsLessThan,
    competitiveMentions,
    afterCursor,
  }: SearchCreatorsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  const accounts = {
    instagram: {
      status: instagramConnectionStatus,
      followersGreaterThan: instagramFollowersGreaterThan,
      followersLessThan: instagramFollowersLessThan,
      interests: instagramInterests,
      isBusiness: instagramIsBusiness,
      handle: instagramHandle,
    },
    tiktok: {
      status: tiktokConnectionStatus,
      followersGreaterThan: tiktokFollowersGreaterThan,
      followersLessThan: tiktokFollowersLessThan,
      minEngagementRate: tiktokMinEngagementRate,
      maxEngagementRate: tiktokMaxEngagementRate,
      ttcmStatus: tiktokTTCMStatus,
      handle: tiktokHandle,
      interests: tiktokOrganicInterests,
      paidInterests: tiktokPaidInterests,
      minCreatorRate: tiktokMinCreatorRate,
      maxCreatorRate: tiktokMaxCreatorRate,
      creatorCurrency: tiktokCreatorCurrency,
      minMedianViews: tiktokMinMedianViews,
      maxMedianViews: tiktokMaxMedianViews,
      audience: {
        gender: tiktokAudienceGender,
        age: tiktokAudienceAge,
      },
    },
    youtube: {
      handle: youtubeHandle,
      followersGreaterThan: youtubeFollowersGreaterThan,
      followersLessThan: youtubeFollowersLessThan,
      videoCountGreaterThan: youtubeVideoCountGreaterThan,
      videoCountLessThan: youtubeVideoCountLessThan,
      viewsGreaterThan: youtubeViewsGreaterThan,
      viewsLessThan: youtubeViewsLessThan,
    },
  };

  const postType =
    ((instagramPostType?.length ?? 0) > 0 ? instagramPostType : undefined) ||
    ((youtubePostType?.length ?? 0) > 0 ? youtubePostType : undefined);

  return axios.put(
    `/brands/${brandId}/relationships`,
    {
      limit,
      sort,
      lookupName,
      restrictToPlatforms,
      postsPublishedStartDate,
      postsPublishedEndDate,
      tagFilterType,
      tags,
      locations: creatorLocations,
      hasMentionedBrand,
      creatorIds,
      postType,
      accounts,
      competitiveMentions,
      afterCursor,
    },
    { ...axiosConfig },
  );
}

export async function findOrCreateCreators(
  { brandId, creators }: FindOrCreateCreatorsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post(`/brands/${brandId}/creators`, creators, { ...axiosConfig });
}

export async function createRelationships({ url, brandId, creatorIds }: CreateRelationshipsParams) {
  return axios.post(url ?? `/brands/${brandId}/relationships`, { creatorIds });
}

export async function getRelationshipTags(
  { brandIds, limit = 60, offset = 0, nextUrl = null }: GetRelationshipTagsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  if (!Array.isArray(brandIds) || brandIds.length === 0) {
    throw new Error('brandIds must be a non-empty array');
  }

  const params = {
    brand_ids: brandIds.join(','),
    limit,
    offset,
  };

  if (nextUrl) return axios.get(nextUrl, axiosConfig);

  return axios.get('/relationships/tags', {
    params,
    ...axiosConfig,
  });
}

export async function searchRelationships(
  params: SearchRelationshipsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  const { brandIds, limit = 60, afterCursor = null } = params;

  if (!Array.isArray(brandIds) || brandIds.length === 0) {
    throw new Error('brandIds must be a non-empty array');
  }

  const payload: Record<string, unknown> = {
    brand_ids: brandIds,
    limit,
    after_cursor: afterCursor,
  };

  Object.keys(payload).forEach((key) => {
    if (payload[key] === null || payload[key] === undefined) {
      delete payload[key];
    }
  });
  return axios.put('/relationships', payload, { ...axiosConfig });
}

export async function fetchCreatorsCount({ brandId }: FetchCreatorsCountParams) {
  return axios.put(`/brands/${brandId}/relationships`);
}

export async function fetchAccountPosts(
  {
    brandId,
    source,
    handle,
    cache,
    limit,
    afterCursor,
    includeAuthorized,
    includeAllMetrics,
  }: FetchAccountPostsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get('/posts', {
    ...axiosConfig,
    params: {
      brandId,
      source,
      handle,
      cache,
      limit,
      afterCursor,
      includeAuthorized,
      includeAllMetrics,
    },
  });
}

export async function fetchAccountMetrics(
  { source, handle, brandId }: FetchAccountMetricsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get(`/accounts/${source}/metrics`, {
    ...axiosConfig,
    params: { brandId, handle },
  });
}

export async function fetchAccountAvgWeeklyPosts(
  { source, handle, brandId }: FetchAccountAvgWeeklyPostsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get(`/accounts/${source}/metrics/avg_weekly_posts`, {
    ...axiosConfig,
    params: { brandId, handle },
  });
}

export async function fetchAudienceMetrics(
  { source, handle, brandId, metric }: FetchAudienceMetricsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get(`/accounts/${source}/audience-metrics`, {
    ...axiosConfig,
    params: { brandId, handle, metric },
  });
}

export async function fetchSimilarCreators(
  { brandId, source, handle, sourceCreatorId }: FetchSimilarCreatorsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get(`/accounts/${source}/similar`, {
    ...axiosConfig,
    params: { brandId, handle, sourceCreatorId },
  });
}

export async function addRelationshipAccount({
  brandId,
  creatorId,
  handle,
  source,
}: AddRelationshipAccountParams) {
  return axios.post(`/brands/${brandId}/relationships/add_account`, {
    creatorId,
    handle,
    source,
  });
}

export async function removeRelationshipAccount({
  brandId,
  creatorId,
  source,
}: RemoveRelationshipAccountParams) {
  return axios.post(`/brands/${brandId}/relationships/remove_account`, {
    creatorId,
    source,
  });
}

export async function fetchCustomFields(
  { brandId }: FetchCustomFieldsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get(`/brands/${brandId}/relationships/custom_fields`, {
    ...axiosConfig,
    params: {},
  });
}

export async function createCustomFields(
  { brandId, customFields }: CreateCustomFieldsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post(
    `/brands/${brandId}/relationships/custom_fields`,
    { customFields },
    { ...axiosConfig },
  );
}

export async function updateCustomField(
  { brandId, fieldId, name, showInRequestForm }: UpdateCustomFieldParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.put(
    `/brands/${brandId}/relationships/custom_fields/${fieldId}`,
    { name, showInRequestForm },
    { ...axiosConfig },
  );
}

export async function deleteCustomField(
  { brandId, fieldId }: DeleteCustomFieldParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.delete(`/brands/${brandId}/relationships/custom_fields/${fieldId}`, {
    ...axiosConfig,
  });
}

export async function fetchMetaPaidPartnersStatus(
  { handle, brandId, brandInstagramId }: FetchMetaPaidPartnersStatusParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get(`/brands/${brandId}/meta_paid_partners`, {
    ...axiosConfig,
    params: { brandInstagramId, handle },
  });
}

export async function requestMetaPaidPartners(
  { handle, brandId, brandInstagramId }: RequestMetaPaidPartnersParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post(
    `/brands/${brandId}/meta_paid_partners`,
    { brandInstagramId, handle },
    { ...axiosConfig },
  );
}

export async function revokeMetaPaidPartners(
  { handle, brandId, brandInstagramId, creatorInstagramId }: RevokeMetaPaidPartnersParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.delete(`/brands/${brandId}/meta_paid_partners`, {
    ...axiosConfig,
    params: { brandInstagramId, creatorInstagramId, handle },
  });
}

export async function createManualPayment({
  brandId,
  tiktokHandle,
  instagramHandle,
  campaignName,
  campaignStartDate,
  campaignEndDate,
  recipientType,
  recipientFirstName,
  recipientLastName,
  recipientBusinessName,
  recipientEmail,
  currency,
  amount,
}: CreateManualPaymentParams) {
  return axios.post('/payments/manual-payments', {
    brandId,
    tiktokHandle,
    instagramHandle,
    campaignName,
    campaignStartDate,
    campaignEndDate,
    recipientType,
    recipientFirstName,
    recipientLastName,
    recipientBusinessName,
    recipientEmail,
    currency,
    amount,
  });
}

export async function downloadCreatorDetails({
  brandId,
  socketId,
  creatorIds,
  hasMentionedBrand,
  instagramConnectionStatus,
  instagramFollowersGreaterThan,
  instagramFollowersLessThan,
  instagramIsBusiness,
  instagramInterests,
  instagramPostType,
  lookupName,
  postsPublishedEndDate,
  postsPublishedStartDate,
  restrictToPlatforms,
  sort,
  tagFilterType,
  tags,
  tiktokAudienceAge,
  tiktokAudienceGender,
  tiktokAudienceRegions,
  tiktokConnectionStatus,
  tiktokFollowersGreaterThan,
  tiktokFollowersLessThan,
  tiktokTTCMStatus,
  tiktokMinCreatorRate,
  tiktokMaxCreatorRate,
  tiktokCreatorCurrency,
  tiktokMinMedianViews,
  tiktokMaxMedianViews,
  tiktokMinEngagementRate,
  tiktokMaxEngagementRate,
  tiktokPaidInterests,
  tiktokOrganicInterests,
  campaignName,
}: DownloadCreatorDetailsParams) {
  return axios.post(`/brands/${brandId}/relationships/creator_details_csv`, {
    socketId,
    creatorIds,
    hasMentionedBrand,
    lookupName,
    postType: instagramPostType,
    postsPublishedEndDate,
    postsPublishedStartDate,
    restrictToPlatforms,
    sort,
    tagFilterType,
    tags,
    campaignName,
    accounts: {
      instagram: {
        status: instagramConnectionStatus,
        followersGreaterThan: instagramFollowersGreaterThan,
        followersLessThan: instagramFollowersLessThan,
        interests: instagramInterests,
        isBusiness: instagramIsBusiness,
      },
      tiktok: {
        status: tiktokConnectionStatus,
        followersGreaterThan: tiktokFollowersGreaterThan,
        followersLessThan: tiktokFollowersLessThan,
        ttcmStatus: tiktokTTCMStatus,
        minCreatorRate: tiktokMinCreatorRate,
        maxCreatorRate: tiktokMaxCreatorRate,
        creatorCurrency: tiktokCreatorCurrency,
        minMedianViews: tiktokMinMedianViews,
        maxMedianViews: tiktokMaxMedianViews,
        minEngagementRate: tiktokMinEngagementRate,
        maxEngagementRate: tiktokMaxEngagementRate,
        paidInterests: tiktokPaidInterests,
        interests: tiktokOrganicInterests,
        audience: {
          regions: tiktokAudienceRegions,
          gender: tiktokAudienceGender,
          age: tiktokAudienceAge,
        },
      },
    },
  });
}

export async function downloadCreatorPosts({
  brandId,
  socketId,
  creatorIds,
  creatorLocations,
  hasMentionedBrand,
  instagramConnectionStatus,
  instagramFollowersGreaterThan,
  instagramFollowersLessThan,
  instagramIsBusiness,
  instagramInterests,
  instagramPostType,
  lookupName,
  postsPublishedEndDate,
  postsPublishedStartDate,
  restrictToPlatforms,
  sort,
  tagFilterType,
  tags,
  tiktokAudienceAge,
  tiktokAudienceGender,
  tiktokAudienceRegions,
  tiktokConnectionStatus,
  tiktokFollowersGreaterThan,
  tiktokFollowersLessThan,
  tiktokMinEngagementRate,
  tiktokMaxEngagementRate,
  tiktokInterests,
  tiktokTTCMStatus,
  ttoCampaignId,
}: DownloadCreatorPostsParams) {
  return axios.put(`/brands/${brandId}/relationships/export_posts`, {
    socketId,
    creatorIds,
    sort,
    lookupName,
    restrictToPlatforms,
    postsPublishedStartDate,
    postsPublishedEndDate,
    tagFilterType,
    tags,
    creatorLocations,
    hasMentionedBrand,
    ttoCampaignId,
    postType: instagramPostType,
    accounts: {
      instagram: {
        status: instagramConnectionStatus,
        followersGreaterThan: instagramFollowersGreaterThan,
        followersLessThan: instagramFollowersLessThan,
        interests: instagramInterests,
        isBusiness: instagramIsBusiness,
      },
      tiktok: {
        status: tiktokConnectionStatus,
        followersGreaterThan: tiktokFollowersGreaterThan,
        followersLessThan: tiktokFollowersLessThan,
        minEngagementRate: tiktokMinEngagementRate,
        maxEngagementRate: tiktokMaxEngagementRate,
        ttcmStatus: tiktokTTCMStatus,
        interests: tiktokInterests,
        audience: {
          regions: tiktokAudienceRegions,
          gender: tiktokAudienceGender,
          age: tiktokAudienceAge,
        },
      },
    },
  });
}

export const fetchShopifyProductList = ({
  brandId,
  limit,
  name,
  sku,
  nameOrSku,
  category,
  type,
  price,
  inventoryCount,
  afterCursor,
}: FetchShopifyProductListParams) =>
  axios.put(`/brands/${brandId}/shopify/products`, {
    limit,
    filters: {
      name,
      sku,
      nameOrSku,
      category,
      type,
      price,
      inventoryCount,
    },
    afterCursor,
  });

export const fetchShopifyProductCategories = ({ brandId }: FetchShopifyProductCategoriesParams) =>
  axios.get(`/brands/${brandId}/shopify/product_categories`);

export const fetchShopifyProductTypes = ({ brandId }: FetchShopifyProductCategoriesParams) =>
  axios.get(`/brands/${brandId}/shopify/product_types`);

export async function fetchShopifyGiftOrders(
  {
    brandId,
    creatorIds,
    orderIds,
    summary = false,
    includeDrafts = false,
    limit,
    offset,
    campaignId,
  }: FetchShopifyGiftOrdersParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get(`/brands/${brandId}/shopify/orders`, {
    ...axiosConfig,
    params: {
      campaignId,
      creatorIds: creatorIds?.join(','),
      orderIds: orderIds?.join(','),
      summary,
      includeDrafts,
      limit,
      offset,
    },
  });
}

export async function createShopifyGiftOrder(
  { brandId, creatorIds, productVariants, campaignId }: CreateShopifyGiftOrderParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post(
    `/brands/${brandId}/shopify/orders`,
    { creatorIds, productVariants, campaignId },
    { ...axiosConfig },
  );
}

export async function disconnectShopifyAccount(
  { brandId }: ShopifyAccountParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.delete(`/brands/${brandId}/shopify/webhooks`, axiosConfig);
}

export async function connectShopifyAccount(
  { brandId }: ShopifyAccountParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post(`/brands/${brandId}/shopify/connected`, axiosConfig);
}

export async function fetchInfluentialFans(
  { brandId, source, sort, limit, afterCursor }: FetchInfluentialFansParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get(`/accounts/${source}/brands/${brandId}/influential_fans`, {
    ...axiosConfig,
    params: {
      sort,
      limit,
      afterCursor,
    },
  });
}

export async function fetchCompetitiveMentions(
  {
    brandId,
    creatorId,
    limit,
    offset,
    sort,
    sources,
    competitorIds,
    isKnownCompetitor,
    excludedBrandAccountIds,
  }: FetchCompetitiveMentionsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  const payload = {
    sources,
    competitorIds,
    isKnownCompetitor,
    excludedBrandAccountIds,
  };

  return axios.put(`/brands/${brandId}/relationships/${creatorId}/competitive_mentions`, payload, {
    ...axiosConfig,
    params: { limit, offset, sort },
  });
}

export async function fetchCompetitiveMentionPosts(
  {
    brandId,
    creatorId,
    sources,
    competitorIds,
    limit,
    offset,
    excludedBrandAccountIds,
  }: FetchCompetitiveMentionPostsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  const payload = {
    sources,
    competitorIds,
    excludedBrandAccountIds,
  };

  return axios.put(
    `/brands/${brandId}/relationships/${creatorId}/competitive_mentions/posts`,
    payload,
    {
      ...axiosConfig,
      params: { limit, offset },
    },
  );
}

export async function fetchBrandedContent(
  { brandId, handle, source }: FetchBrandedContentParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get(`/accounts/${source}/branded_content`, {
    ...axiosConfig,
    params: { brandId, handle },
  });
}

export async function fetchCreatorAiInsights(
  { brandId, creatorId }: FetchCreatorAiInsightsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get(`/brands/${brandId}/relationships/${creatorId}/ai_profile_insights`, {
    ...axiosConfig,
  });
}

export async function generateCreatorAiInsights(
  { brandId, creatorId }: GenerateCreatorAiInsightsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post(
    `/brands/${brandId}/relationships/${creatorId}/ai_profile_insights`,
    {},
    {
      ...axiosConfig,
    },
  );
}

export async function fetchPastBrandPartnerships(
  { brandId, handle, source }: FetchPastBrandPartnershipsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get(`/accounts/${source}/past_brand_partnerships`, {
    ...axiosConfig,
    params: { brandId, handle },
  });
}

export async function fetchNaturalLanguageSearchAccounts(
  {
    brandId,
    source,
    userRequest,
    visualFeatures,
    restrictToDataSource,
    restrictToDataSourceReason,
  }: FetchNaturalLanguageSearchAccountsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get('/natural_language_search/accounts', {
    ...axiosConfig,
    params: {
      brandId,
      source,
      userRequest,
      visualFeatures,
      restrictToDataSource,
      restrictToDataSourceReason,
    },
  });
}
